/**
 * FileDropzone Component
 *
 * Unified dropzone component for file uploads across the application.
 * Handles all upload states: idle, selected, uploading, processing, completed, failed.
 *
 * Features:
 * - Drag-and-drop file selection with visual feedback
 * - Click to browse files
 * - File type and size validation
 * - Selected file display with clear option
 * - Upload progress indicator
 * - Processing state with spinner
 * - Success/error states with auto-reset
 * - Multiple visual variants (default, compact, inline)
 *
 * @module components/common/FileDropzone
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Upload,
  FileText,
  AlertCircle,
  X,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';
import {
  Button,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@loader/ui';

import { isExcelFile, getExcelSheetNames } from '@/lib/excelUtils';
import {
  validateFile,
  formatFileSize,
  ACCEPTED_FILE_TYPES,
  ALLOWED_FILE_EXTENSIONS,
} from '@/lib/fileUtils';
import { MAX_FILE_SIZE } from '@/config/constants';

// ============================================================================
// TYPES
// ============================================================================

/** Visual state of the upload */
export type UploadState =
  | 'idle'
  | 'selected'
  | 'uploading'
  | 'processing'
  | 'completed'
  | 'failed';

/** Visual variant of the dropzone */
export type DropzoneVariant = 'default' | 'compact' | 'inline';

export interface FileDropzoneProps {
  /** Callback when a valid file is selected */
  onFileSelect: (file: File) => void;
  /** Callback when file is cleared */
  onClearFile?: () => void;
  /** Currently selected file (controlled) */
  selectedFile?: File | null;
  /** Current upload state */
  uploadState?: UploadState;
  /** Upload progress (0-100) */
  uploadProgress?: number;
  /** Error message to display */
  errorMessage?: string;
  /** Accepted file types (defaults to ACCEPTED_FILE_TYPES constant) */
  accept?: string;
  /** Maximum file size in bytes (defaults to MAX_FILE_SIZE constant) */
  maxSize?: number;
  /** Disable the dropzone */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Visual variant */
  variant?: DropzoneVariant;
  /** Status message override */
  statusMessage?: string;
  /**
   * Callback when Excel sheet selection changes.
   * Only called for Excel files with multiple sheets.
   */
  onSheetChange?: (sheet: string) => void;
  /**
   * Currently selected sheet name (controlled).
   * If not provided, first sheet is auto-selected.
   */
  selectedSheet?: string | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * FileDropzone - Unified file upload component
 *
 * @example
 * // Basic file selection
 * <FileDropzone onFileSelect={(file) => handleFile(file)} />
 *
 * @example
 * // With selected file display
 * <FileDropzone
 *   onFileSelect={handleFile}
 *   selectedFile={file}
 *   onClearFile={() => setFile(null)}
 * />
 *
 * @example
 * // With upload progress
 * <FileDropzone
 *   onFileSelect={handleFile}
 *   selectedFile={file}
 *   uploadState="uploading"
 *   uploadProgress={45}
 * />
 *
 * @example
 * // Compact inline variant
 * <FileDropzone
 *   variant="inline"
 *   onFileSelect={handleFile}
 *   uploadState={state}
 *   uploadProgress={progress}
 * />
 */
export function FileDropzone({
  onFileSelect,
  onClearFile,
  selectedFile,
  uploadState = 'idle',
  uploadProgress = 0,
  errorMessage,
  accept = ACCEPTED_FILE_TYPES,
  maxSize = MAX_FILE_SIZE,
  disabled = false,
  className = '',
  variant = 'default',
  statusMessage,
  onSheetChange,
  selectedSheet,
}: FileDropzoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sheet extraction state (internal)
  const [sheets, setSheets] = useState<string[] | null>(null);
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);

  // ========================================================================
  // EXCEL SHEET EXTRACTION
  // ========================================================================

  /**
   * Extract sheet names when an Excel file is selected.
   * Auto-selects the first sheet if no sheet is currently selected.
   */
  useEffect(() => {
    // Reset sheets when file changes or is cleared
    if (!selectedFile) {
      setSheets(null);
      return;
    }

    // Only extract sheets for Excel files
    if (!isExcelFile(selectedFile)) {
      setSheets(null);
      return;
    }

    // Extract sheet names
    const extractSheets = async () => {
      setIsLoadingSheets(true);
      try {
        const sheetNames = await getExcelSheetNames(selectedFile);
        setSheets(sheetNames);

        // Auto-select first sheet if none selected and callback provided
        if (sheetNames.length > 0 && !selectedSheet && onSheetChange) {
          onSheetChange(sheetNames[0]);
        }
      } catch (error) {
        console.error('Failed to extract Excel sheets:', error);
        setSheets(null);
      } finally {
        setIsLoadingSheets(false);
      }
    };

    extractSheets();
  }, [selectedFile, selectedSheet, onSheetChange]);

  // Determine effective state
  const effectiveState: UploadState = selectedFile && uploadState === 'idle'
    ? 'selected'
    : uploadState;

  const isInteractive = effectiveState === 'idle' && !disabled;
  const showProgress = effectiveState === 'uploading' || effectiveState === 'processing';

  // ========================================================================
  // DRAG AND DROP HANDLERS
  // ========================================================================

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isInteractive) {
        setDragActive(true);
      }
    },
    [isInteractive]
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
    },
    []
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isInteractive) {
        setDragActive(true);
      }
    },
    [isInteractive]
  );

  // ========================================================================
  // FILE SELECTION HANDLERS
  // ========================================================================

  const handleFileSelection = useCallback(
    (file: File) => {
      setValidationError(null);

      const validation = validateFile(file, maxSize);
      if (!validation.isValid) {
        setValidationError(validation.errors[0] || 'Invalid file');
        return;
      }

      onFileSelect(file);
    },
    [maxSize, onFileSelect]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (!isInteractive) return;

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelection(file);
      }
    },
    [isInteractive, handleFileSelection]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelection(file);
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [handleFileSelection]
  );

  const handleBrowseClick = useCallback(() => {
    if (isInteractive) {
      inputRef.current?.click();
    }
  }, [isInteractive]);

  const handleClick = useCallback(() => {
    if (isInteractive) {
      handleBrowseClick();
    }
  }, [isInteractive, handleBrowseClick]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && isInteractive) {
        e.preventDefault();
        handleBrowseClick();
      }
    },
    [isInteractive, handleBrowseClick]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setValidationError(null);
      onClearFile?.();
    },
    [onClearFile]
  );

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  const displayError = errorMessage || validationError;

  const getStatusMessage = (): string => {
    if (statusMessage) return statusMessage;
    switch (effectiveState) {
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Processing...';
      case 'completed':
        return 'Upload complete!';
      case 'failed':
        return displayError || 'Upload failed';
      default:
        return '';
    }
  };

  const getIcon = () => {
    switch (effectiveState) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-destructive" />;
      case 'uploading':
      case 'processing':
        return <Loader2 className="h-5 w-5 text-primary animate-spin" />;
      case 'selected':
        return <FileText className="h-5 w-5 text-primary" />;
      default:
        return <Upload className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getContainerClasses = (): string => {
    const base = 'rounded-lg border-2 border-dashed transition-all';

    // Variant-specific spacing
    const spacing = {
      default: 'p-8',
      compact: 'p-4',
      inline: 'px-4 py-3',
    }[variant];

    // State-specific styling
    let stateStyles = '';
    if (effectiveState === 'failed' || displayError) {
      stateStyles = 'border-destructive/50 bg-destructive/5';
    } else if (effectiveState === 'completed') {
      stateStyles = 'border-green-500/50 bg-green-500/5';
    } else if (effectiveState === 'selected') {
      stateStyles = 'border-primary/50 bg-primary/5';
    } else if (dragActive) {
      stateStyles = 'border-primary bg-primary/10';
    } else if (showProgress) {
      stateStyles = 'border-primary/30 bg-muted/50';
    } else {
      stateStyles = 'border-border';
    }

    // Interactivity
    const interactivity = isInteractive
      ? 'cursor-pointer hover:border-primary/50 hover:bg-muted/30'
      : disabled
        ? 'opacity-50 cursor-not-allowed'
        : '';

    return `${base} ${spacing} ${stateStyles} ${interactivity} ${className}`;
  };

  // ========================================================================
  // RENDER: IDLE STATE (DEFAULT VARIANT)
  // ========================================================================

  const renderIdleDefault = () => (
    <div className="space-y-4 text-center">
      <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
      <div>
        <p className="text-lg font-medium text-foreground">Drop your file here</p>
        <p className="text-sm text-muted-foreground">or click to browse files</p>
      </div>
      <Button
        variant="outline"
        onClick={(e) => {
          e.stopPropagation();
          handleBrowseClick();
        }}
        type="button"
      >
        <FileText className="mr-2 h-4 w-4" />
        Select File
      </Button>
      <div className="text-xs text-muted-foreground">
        <p>Supported: {ALLOWED_FILE_EXTENSIONS.map((ext) => ext.replace('.', '')).join(', ')}</p>
        <p>Maximum size: {formatFileSize(maxSize)}</p>
      </div>
    </div>
  );

  // ========================================================================
  // RENDER: IDLE STATE (COMPACT VARIANT)
  // ========================================================================

  const renderIdleCompact = () => (
    <div className="flex items-center justify-center gap-3">
      <Upload className="h-5 w-5 text-muted-foreground" />
      <div className="text-left">
        <p className="text-sm font-medium text-foreground">Drop file or click to browse</p>
        <p className="text-xs text-muted-foreground">
          {ALLOWED_FILE_EXTENSIONS.map((ext) => ext.replace('.', '')).join(', ')} - Max{' '}
          {formatFileSize(maxSize)}
        </p>
      </div>
    </div>
  );

  // ========================================================================
  // RENDER: IDLE STATE (INLINE VARIANT)
  // ========================================================================

  const renderIdleInline = () => (
    <div className="flex items-center gap-3">
      <Upload className="h-5 w-5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">Drop file or click to upload</p>
        <p className="text-xs text-muted-foreground">.csv, .json, .xlsx, .parquet</p>
      </div>
    </div>
  );

  // ========================================================================
  // RENDER: SHEET SELECTOR (for Excel files with multiple sheets)
  // ========================================================================

  const renderSheetSelector = () => {
    // Only show for Excel files with multiple sheets
    if (!sheets || sheets.length <= 1 || !onSheetChange) return null;

    return (
      <Select
        value={selectedSheet ?? sheets[0]}
        onValueChange={onSheetChange}
        disabled={disabled || isLoadingSheets}
      >
        <SelectTrigger
          className="h-7 w-auto min-w-24 max-w-32 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          {isLoadingSheets ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <SelectValue placeholder="Sheet" />
          )}
        </SelectTrigger>
        <SelectContent>
          {sheets.map((sheet) => (
            <SelectItem key={sheet} value={sheet} className="text-xs">
              {sheet}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  // ========================================================================
  // RENDER: SELECTED FILE STATE
  // ========================================================================

  const renderSelected = () => {
    if (!selectedFile) return null;

    if (variant === 'inline') {
      return (
        <div className="flex items-center gap-3">
          <div className="shrink-0">{getIcon()}</div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">{selectedFile.name}</p>
            <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
          </div>
          {/* Sheet selector for Excel files */}
          {renderSheetSelector()}
          {onClearFile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClear}
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Remove file</span>
            </Button>
          )}
        </div>
      );
    }

    // Default and compact variants
    return (
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-foreground truncate">{selectedFile.name}</p>
            <p className="text-sm text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Sheet selector for Excel files */}
          {renderSheetSelector()}
          {onClearFile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClear}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Remove file</span>
            </Button>
          )}
        </div>
      </div>
    );
  };

  // ========================================================================
  // RENDER: PROGRESS STATE (UPLOADING/PROCESSING)
  // ========================================================================

  const renderProgress = () => {
    const message = getStatusMessage();

    if (variant === 'inline') {
      return (
        <div className="flex items-center gap-3">
          <div className="shrink-0">{getIcon()}</div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">{message}</p>
              <span className="text-xs text-muted-foreground">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="mt-1.5 h-1.5" />
          </div>
        </div>
      );
    }

    // Default and compact variants
    return (
      <div className="space-y-3 text-center">
        <div className="flex justify-center">{getIcon()}</div>
        <div>
          <p className="text-sm font-medium text-foreground">{message}</p>
          {selectedFile && (
            <p className="text-xs text-muted-foreground mt-1">{selectedFile.name}</p>
          )}
        </div>
        <Progress value={uploadProgress} className="h-2 max-w-xs mx-auto" />
        <p className="text-xs text-muted-foreground">{uploadProgress}%</p>
      </div>
    );
  };

  // ========================================================================
  // RENDER: COMPLETED STATE
  // ========================================================================

  const renderCompleted = () => {
    const message = getStatusMessage();

    if (variant === 'inline') {
      return (
        <div className="flex items-center gap-3">
          <div className="shrink-0">{getIcon()}</div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-green-600">{message}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2 text-center">
        <div className="flex justify-center">{getIcon()}</div>
        <p className="text-sm font-medium text-green-600">{message}</p>
      </div>
    );
  };

  // ========================================================================
  // RENDER: FAILED STATE
  // ========================================================================

  const renderFailed = () => {
    const message = getStatusMessage();

    if (variant === 'inline') {
      return (
        <div className="flex items-center gap-3">
          <div className="shrink-0">{getIcon()}</div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-destructive">{message}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2 text-center">
        <div className="flex justify-center">{getIcon()}</div>
        <p className="text-sm font-medium text-destructive">{message}</p>
      </div>
    );
  };

  // ========================================================================
  // RENDER CONTENT BASED ON STATE
  // ========================================================================

  const renderContent = () => {
    switch (effectiveState) {
      case 'selected':
        return renderSelected();
      case 'uploading':
      case 'processing':
        return renderProgress();
      case 'completed':
        return renderCompleted();
      case 'failed':
        return renderFailed();
      default:
        // Idle state - render based on variant
        if (variant === 'inline') return renderIdleInline();
        if (variant === 'compact') return renderIdleCompact();
        return renderIdleDefault();
    }
  };

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  return (
    <div className="space-y-2">
      <div
        className={getContainerClasses()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : -1}
        aria-label={isInteractive ? 'Upload file' : undefined}
      >
        {renderContent()}
      </div>

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleFileInput}
        accept={accept}
        disabled={disabled || !isInteractive}
        aria-hidden="true"
      />

      {/* Validation error (only in idle state) */}
      {effectiveState === 'idle' && validationError && (
        <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{validationError}</p>
        </div>
      )}
    </div>
  );
}
